import React from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { createGlobalStyle } from 'styled-components';

import Layout from './components/Layout';
import ProtectedRoute, { RequirePermission } from './components/ProtectedRoute';
import Dashboard from './pages/Dashboard';
import RelatorioVendas from './pages/RelatorioVendas';
import RelatorioMediaVendasVendedor from './pages/RelatorioMediaVendasVendedor';
import RelatorioComissoes from './pages/RelatorioComissoes';
import RelatorioHeatmapVendas from './pages/RelatorioHeatmapVendas';
import RelatorioTelemarketingPneusVendas from './pages/RelatorioTelemarketingPneusVendas';
import RelatorioPneusEstoqueAlerta from './pages/RelatorioPneusEstoqueAlerta';
import RelatorioPneusPorNc from './pages/RelatorioPneusPorNc';
import RelatorioPneusSemObrigatorios from './pages/RelatorioPneusSemObrigatorios';
import RelatorioProdutos from './pages/RelatorioProdutos';
import { FilialProvider } from './context/FilialContext';
import { AuthProvider } from './context/AuthContext';
import Login from './pages/Login';
import Ponto from './pages/Ponto';
import PontoIrregularidadesManha from './pages/PontoIrregularidadesManha';
import PontoEsquecimento from './pages/PontoEsquecimento';
import PontoAtrasos from './pages/PontoAtrasos';
import AlterarPreco from './pages/AlterarPreco';
import ProdutosPorFilial from './pages/ProdutosPorFilial';
import Usuarios from './pages/Usuarios';
import EmailConfig from './pages/EmailConfig';
import EmailSchedules from './pages/EmailSchedules';
import CallMeBotNumbers from './pages/CallMeBotNumbers';
import HoleriteAssinatura from './pages/HoleriteAssinatura';
import PontoEscalas from './pages/PontoEscalas';
import TireAlertRules from './pages/TireAlertRules';

// Estilos globais
const GlobalStyle = createGlobalStyle`
  * {
    margin: 0;
    padding: 0;
    box-sizing: border-box;
  }

  body {
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen',
      'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue',
      sans-serif;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    background-color: #f9fafb;
    color: #111827;
    line-height: 1.5;
  }

  code {
    font-family: source-code-pro, Menlo, Monaco, Consolas, 'Courier New',
      monospace;
  }

  /* Animações */
  .animate-spin {
    animation: spin 1s linear infinite;
  }

  @keyframes spin {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  }

  /* Scrollbar personalizada */
  ::-webkit-scrollbar {
    width: 8px;
  }

  ::-webkit-scrollbar-track {
    background: #f1f5f9;
  }

  ::-webkit-scrollbar-thumb {
    background: #cbd5e1;
    border-radius: 4px;
  }

  ::-webkit-scrollbar-thumb:hover {
    background: #94a3b8;
  }

  /* Foco acessível */
  button:focus,
  input:focus,
  select:focus,
  textarea:focus {
    outline: 2px solid #3b82f6;
    outline-offset: 2px;
  }
`;

// Configuração do React Query
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutos
      gcTime: 10 * 60 * 1000, // 10 minutos
      retry: (failureCount, error: any) => {
        // Não tenta novamente para erros 4xx
        if (error?.response?.status >= 400 && error?.response?.status < 500) {
          return false;
        }
        return failureCount < 3;
      },
      refetchOnWindowFocus: false,
    },
    mutations: {
      retry: 1,
    },
  },
});

const App: React.FC = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <Router>
          <FilialProvider>
          <GlobalStyle />
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/" element={<ProtectedRoute><Layout /></ProtectedRoute>}>
              <Route index element={<Dashboard />} />
              {/* Relatórios */}
              <Route path="relatorios/vendas" element={<RelatorioVendas />} />
              <Route path="relatorios/media-vendas-vendedor" element={<RelatorioMediaVendasVendedor />} />
              <Route path="relatorios/comissoes" element={<RelatorioComissoes />} />
              <Route path="relatorios/heatmap-vendas" element={<RelatorioHeatmapVendas />} />
              <Route path="relatorios/telemarketing-pneus-vendas" element={<RelatorioTelemarketingPneusVendas />} />
              <Route path="relatorios/pneus-estoque-alerta" element={<RelatorioPneusEstoqueAlerta />} />
              <Route path="relatorios/pneus-por-nc" element={<RelatorioPneusPorNc />} />
              <Route path="relatorios/pneus-sem-obrigatorios" element={<RelatorioPneusSemObrigatorios />} />
              <Route path="relatorios/produtos" element={<RelatorioProdutos />} />
              <Route path="relatorios/clientes" element={<div>Relatório de Clientes em construção</div>} />
              <Route path="relatorios/ponto" element={<Ponto />} />
              <Route path="relatorios/ponto/irregularidades-manha" element={<PontoIrregularidadesManha />} />
              <Route path="relatorios/ponto/esquecimento" element={<PontoEsquecimento />} />
              <Route path="relatorios/ponto/atrasos" element={<PontoAtrasos />} />
              <Route path="tabelas" element={<div>Explorador de Tabelas em construção</div>} />
              <Route path="admin/alterar-preco" element={<AlterarPreco />} />
              <Route
                path="admin/produtos-por-filial"
                element={
                  <RequirePermission permission="produtos_por_filial">
                    <ProdutosPorFilial />
                  </RequirePermission>
                }
              />
              <Route path="admin/usuarios" element={<Usuarios />} />
              <Route path="admin/email-config" element={<EmailConfig />} />
              <Route path="admin/schedules" element={<EmailSchedules />} />
              <Route path="admin/tire-alert-rules" element={<TireAlertRules />} />
              <Route path="admin/callmebot" element={<CallMeBotNumbers />} />
              <Route path="admin/ponto-escalas" element={<PontoEscalas />} />
              <Route path="assinaturas/holerite" element={<HoleriteAssinatura />} />
            </Route>
          </Routes>

          {/* Configuração do Toast */}
          <Toaster
            position="top-right"
            toastOptions={{
              duration: 4000,
              style: {
                background: '#363636',
                color: '#fff',
                fontSize: '14px',
                padding: '12px 16px',
                borderRadius: '8px',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
              },
              success: {
                iconTheme: {
                  primary: '#10b981',
                  secondary: '#fff',
                },
              },
              error: {
                iconTheme: {
                  primary: '#ef4444',
                  secondary: '#fff',
                },
              },
            }}
          />
          </FilialProvider>
        </Router>
      </AuthProvider>
    </QueryClientProvider>
  );
};

export default App;